import React, { useCallback, useEffect, useState } from 'react'
import { useCombobox, UseComboboxStateChange } from 'downshift'
import { useDispatch, useSelector } from 'react-redux'
import { debounce } from 'lodash'
import { TextInput } from '../styled/TextInput/TextInput'
import { GeoItem } from '../../@types/geoEntity'
import { ReduxState } from '../../store/reducers'
import { fetchLocation } from '../../store/location/thunks'
import { gacApiFetch } from '../../clients/geoClient'
import { resetLocation } from '../../store/location/locationSlice'

interface LocationProps {
  onValidationChange: (valid: boolean, value?: string) => void
  onError: () => void
  autofocus?: boolean
}

const locationRegex = /(\d{5})\s(.+)$/
const unexpectedCharacterRegex = /[-._!"`'#%&,:;<>=@{}~$()*+/\\?[\]^|]+/

const Location = ({ onError, onValidationChange, autofocus = true }: LocationProps) => {
  const {
    config: { theme },
    location: { data: location, status },
  } = useSelector((state: ReduxState) => state)
  const [postcodes, setPostcodes] = useState<Array<GeoItem>>([])
  const [inputValue, setInputValue] = useState<string | null | undefined>(location?.postcode ?? '')
  const [city, setCity] = useState<string>('')
  const [lastQuery, setLastQuery] = useState<string>('')

  const isValidLocationSelected = !!status && !!location?.coordinates && !!inputValue && inputValue.length == 5
  const hideSuggestionsMenu = isValidLocationSelected && inputValue === location?.postcode
  const hasUnexpectedCharacters = inputValue && unexpectedCharacterRegex.test(inputValue)
  const isInvalidPostcode =
    (!!inputValue && postcodes.length === 0 && inputValue === lastQuery) || hasUnexpectedCharacters

  const dispatch = useDispatch()

  useEffect(() => {
    onValidationChange(isValidLocationSelected, location?.postcode)
  }, [isValidLocationSelected])

  useEffect(() => {
    if (inputValue) {
      if (!!location && inputValue !== location.postcode) {
        dispatch(resetLocation())
      }
      if (hasUnexpectedCharacters) {
        return
      }
      inputChanged(inputValue)
    }
  }, [inputValue])

  const handleQueryChanged = async (query: string) => {
    try {
      const postcodes = await gacApiFetch(query)
      setPostcodes(postcodes)

      if (postcodes.length === 1) {
        handleSelect(query, postcodes[0])
      }
      setLastQuery(query)
    } catch {
      onError()
    }
  }

  const inputChanged = useCallback(debounce(handleQueryChanged, 250), [])

  const onStateChange = (change: UseComboboxStateChange<GeoItem>) => {
    if (
      Object.prototype.hasOwnProperty.call(change, 'inputValue') &&
      change.type === useCombobox.stateChangeTypes.InputChange
    ) {
      setCity('')
      setInputValue(change.inputValue)
    }
  }

  const handleSelect = (value: string, item: GeoItem) => {
    const parsedResult = item.entity.label.match(locationRegex)

    if (parsedResult !== null) {
      const [, postcode, city] = parsedResult

      setInputValue(postcode)
      setCity(city)
      setPostcodes([item])

      dispatch(fetchLocation(item.entity.id))
    }
  }

  const onSelectedItemChange = (change: UseComboboxStateChange<GeoItem>) => {
    if (!change.selectedItem) return
    handleSelect('', change.selectedItem)
  }

  const { getItemProps, getInputProps, getMenuProps, isOpen, highlightedIndex, getComboboxProps } = useCombobox({
    onStateChange: onStateChange,
    items: postcodes,
    inputValue: inputValue || '',
    onSelectedItemChange: onSelectedItemChange,
  })

  const cropLongCity = (city: string) => (city && city.length > 28 ? `${city.substr(0, 28)}...` : city)

  return (
    <div className='autocomplete-container'>
      <span className='input-label align-left'>Postleitzahl</span>
      <div {...getComboboxProps()} className='input-text-container'>
        <span className='location-input-affix font-lightgray location-name'>{cropLongCity(city)}</span>
        <TextInput
          {...getInputProps()}
          id='location-input'
          type='tel'
          maxLength={5}
          className={`input-text location-input`}
          valid={!isInvalidPostcode}
          data-testing='location-input'
          data-testid='location-combobox'
          placeholder='z.B. 10457'
          autoFocus={autofocus}
          label=''
          textColor={theme.text}
          borderColor={theme.secondary}
          backgroundColor={theme.background}
        />
      </div>
      <div
        {...getMenuProps()}
        className={`grid-item  location-suggestion-wrapper ${
          postcodes && postcodes.length && !hideSuggestionsMenu ? '' : 'hide'
        }`}
        data-testid='location-listbox'
      >
        {isOpen && !hideSuggestionsMenu && postcodes.length > 0 && (
          <div className={`location-suggestion-menu`}>
            {postcodes.map((item, index) => {
              const parsedResult = item.entity.label.match(locationRegex)

              if (!parsedResult) {
                return <div key={'test'} />
              }

              const [, postcode, city] = parsedResult

              return (
                <div
                  {...getItemProps({ index, item })}
                  key={item.entity.id}
                  data-testing={item.entity.id}
                  data-testid={`location-option-${item.entity.id}`}
                  className={`grid-item grid-flex location-suggestion-item ${
                    highlightedIndex === index ? 'location-suggestion-item-highlighted' : ''
                  }`}
                >
                  <div className='grid-item one-fifth font-regular'>{postcode}</div>
                  <div className='grid-item four-fifths'>{cropLongCity(city)}</div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default Location
